import React from 'react';
import classNames from 'classnames';

// Styles
import './ImageRenderer.scss';

// Utils
import { getElementCardCropInset, getElementCardRenderedWidthPx } from '../utils/elementUtil';
import { getShowFullAspectRatio } from '../../../common/elements/utils/elementPropertyUtils';
import { multiRef } from '../../utils/multiRef';

// Components
import ImageDrawing from './ImageDrawing';
import ElementImage from '../../components/images/ElementImage';
import PillsPanel from '../pillsPanel/PillsPanel';
import ImageReplacementOverlay from './ImageReplacementOverlay';

// Constants
import { EXPORT_HIGH_QUALITY_IMAGE_WIDTH } from '../../../common/images/imageConstants';
import { IMAGE_TYPES } from '../../../common/media/mediaConstants';

// Types
import { ImMNElement } from '../../../common/elements/elementModelTypes';
import { RectSize } from '../../../common/maths/geometry/rect/rectTypes';

type Props = {
    element: ImMNElement;
    elementId?: string;
    permissions: number;
    inList: string;
    isHovered: boolean;
    gridSize: number;
    renderHighQualityImages: boolean;
    tempMediaSize: RectSize | null;
    isResizingOrAnimating: boolean;
    mediaElementRef: React.Ref<HTMLDivElement>;
    reactions?: object[] | object;
    connectFileDropTarget: React.Ref<HTMLDivElement>;
    isTransparent: boolean;
    isReplaceModeHovered: boolean;
    isPresentationModeEnabled: boolean;
    isPreview: boolean;
    showReactionButton?: boolean;
    openFullSizeImageOrNativeCarousel: (event: React.MouseEvent) => void;
};

/**
 * Renders a 'ProgressiveImage' for an element.
 * If the element has image data the progressive image will use the thumbnail from the image data, otherwise it will
 * use the data from the element's attachment as it must have just been uploaded.
 *
 * NOTE: This component should ONLY be used when there is actually image data (either in the element's content or
 * attachment).  If it doesn't have data this component will not display correctly.
 */
const ImageRenderer = (props: Props) => {
    const {
        element,
        inList,
        isHovered,
        gridSize,
        renderHighQualityImages,
        tempMediaSize,
        isResizingOrAnimating,
        mediaElementRef,
        connectFileDropTarget,
        isTransparent,
        isReplaceModeHovered,
        isPresentationModeEnabled,
        isPreview,
        openFullSizeImageOrNativeCarousel,
    } = props;

    const widthPx = getElementCardRenderedWidthPx(props);
    const imageQualityWidthPxOverride = renderHighQualityImages
        ? // Use full quality images if exporting at high quality
          EXPORT_HIGH_QUALITY_IMAGE_WIDTH
        : widthPx;

    const savedWidthPx = getElementCardRenderedWidthPx({ ...props, tempSize: null });

    const preventCropToGrid = getShowFullAspectRatio(element) || isResizingOrAnimating || isTransparent;

    const cropInset = getElementCardCropInset(inList);

    const handleDoubleClick = (event: React.MouseEvent) => {
        if (isPresentationModeEnabled) return;

        openFullSizeImageOrNativeCarousel(event);
    };

    return (
        <div
            className={classNames('ImageRenderer', { canDrop: isHovered })}
            ref={multiRef([mediaElementRef, connectFileDropTarget])}
            onDoubleClick={handleDoubleClick}
        >
            {isReplaceModeHovered && <ImageReplacementOverlay />}
            <ElementImage
                useSecureMediaUrl
                showBrokenIconOnError
                element={element}
                cropToGrid={!preventCropToGrid}
                cropInset={cropInset}
                gridSize={gridSize}
                imageType={IMAGE_TYPES.ELEMENT}
                widthPx={widthPx}
                imageQualityWidthPxOverride={imageQualityWidthPxOverride}
                savedWidthPx={savedWidthPx}
                forcedSize={tempMediaSize}
                isPreview={isPreview}
            >
                <PillsPanel {...props} />
                <ImageDrawing {...props} />
            </ElementImage>
        </div>
    );
};

export default ImageRenderer;
